'use client';

import { type FormEvent, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { PasswordInput } from '../../../components/ui/PasswordInput';
import { Link } from '../../../components/ui/Link';
import {
  AuthSplitLayout,
  AUTH_LOGIN_LINK_CLASS,
} from '../components/AuthSplitLayout';
import { ForgotPasswordDialog } from '../components/ForgotPasswordDialog';
import { ROUTES } from '../../../constants/routes';
import { themeConfig } from '../../../theme';
import { ValidationError } from 'yup';
import {
  getApiErrorMessage,
  useConfirmSignUpMutation,
  useResendConfirmationMutation,
  useSignInMutation,
  useSignUpMutation,
} from '../hooks/useAuthMutations';
import { consumeAuthExpiredFlashMessage } from '../lib/authExpiredFlash';
import {
  applyLocalDevSuperAdminSession,
  isLocalDevAuthBypassEnabled,
} from '../lib/localDevAuthBypass';
import { signUpSchema, type SignUpFormValues } from '../validation/sign-up';

const emptySignUpValues: SignUpFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

type SignUpFieldErrors = Partial<Record<keyof SignUpFormValues, string>>;

type AuthMode = 'sign-in' | 'sign-up';

const RESEND_COOLDOWN_AFTER_SIGNUP_SEC = 60;
const RESEND_COOLDOWN_AFTER_RESEND_SEC = 60;

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function AuthPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const localDevAuthBypass = isLocalDevAuthBypassEnabled();
  const [signInValues, setSignInValues] = useState({ email: '', password: '' });
  const [signUpValues, setSignUpValues] =
    useState<SignUpFormValues>(emptySignUpValues);
  const [signUpFieldErrors, setSignUpFieldErrors] = useState<SignUpFieldErrors>(
    {},
  );

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifiedBanner, setVerifiedBanner] = useState<string | null>(null);
  const [passwordResetBanner, setPasswordResetBanner] = useState<string | null>(
    null,
  );
  const [emailChangedBanner, setEmailChangedBanner] = useState<string | null>(
    null,
  );
  const [accountDeletedBanner, setAccountDeletedBanner] = useState<
    string | null
  >(null);
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState<
    string | null
  >(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const [authMode, setAuthMode] = useState<AuthMode>('sign-in');

  const signInMutation = useSignInMutation();
  const signUpMutation = useSignUpMutation();
  const confirmMutation = useConfirmSignUpMutation();
  const resendMutation = useResendConfirmationMutation();

  useEffect(() => {
    if (
      signInMutation.isSuccess &&
      signInMutation.data?.success &&
      signInMutation.data.data?.accessToken
    ) {
      router.replace(ROUTES.admin);
    }
  }, [signInMutation.isSuccess, signInMutation.data, router]);

  useEffect(() => {
    const flash = consumeAuthExpiredFlashMessage();
    if (flash) setSessionExpiredBanner(flash);
  }, []);

  useEffect(() => {
    if (searchParams.get('passwordReset') === '1') {
      setPasswordResetBanner(
        'Your password was updated. You can sign in with your new password below.',
      );
      const next = new URLSearchParams(searchParams.toString());
      next.delete('passwordReset');
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    }
  }, [searchParams, pathname, router]);

  useEffect(() => {
    if (searchParams.get('emailChanged') === '1') {
      setEmailChangedBanner(
        'Your email was verified. Sign in using your new email address below.',
      );
      const next = new URLSearchParams(searchParams.toString());
      next.delete('emailChanged');
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    }
  }, [searchParams, pathname, router]);

  useEffect(() => {
    if (searchParams.get('accountDeleted') === '1') {
      setAccountDeletedBanner(
        'Your account was deleted. You can create a new account below if sign-up is enabled.',
      );
      const next = new URLSearchParams(searchParams.toString());
      next.delete('accountDeleted');
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    }
  }, [searchParams, pathname, router]);

  useEffect(() => {
    const raw = searchParams.get('verify');
    if (!raw) return;
    try {
      const email = normalizeEmail(decodeURIComponent(raw));
      if (!email) return;
      setVerificationEmail(email);
      setVerifyOpen(true);
      setAuthMode('sign-up');
      setSignInValues((v) => ({ ...v, email }));
      const next = new URLSearchParams(searchParams.toString());
      next.delete('verify');
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    } catch {
      // ignore malformed param
    }
  }, [searchParams, pathname, router]);

  useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const id = window.setTimeout(
      () => setResendCooldownSec((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => window.clearTimeout(id);
  }, [resendCooldownSec]);

  const signInErrorMessage = signInMutation.error
    ? getApiErrorMessage(signInMutation.error)
    : null;
  const signUpErrorMessage = signUpMutation.error
    ? getApiErrorMessage(signUpMutation.error)
    : null;
  const confirmErrorMessage = confirmMutation.error
    ? getApiErrorMessage(confirmMutation.error)
    : null;
  const resendInfoMessage =
    resendMutation.isSuccess && resendMutation.data?.success
      ? (resendMutation.data?.data?.message ??
        resendMutation.data?.message ??
        'A new code has been sent.')
      : null;

  const openVerification = (email: string, cooldownSec: number) => {
    const e = normalizeEmail(email);
    setVerificationEmail(e);
    setVerifyCode('');
    setVerifyOpen(true);
    setVerifiedBanner(null);
    confirmMutation.reset();
    resendMutation.reset();
    setResendCooldownSec(cooldownSec);
    setSignInValues((v) => ({ ...v, email: e }));
  };

  const closeVerification = () => {
    setVerifyOpen(false);
    setVerifyCode('');
    confirmMutation.reset();
    resendMutation.reset();
    setResendCooldownSec(0);
  };

  const handleSignInSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setVerifiedBanner(null);
    setPasswordResetBanner(null);
    setSessionExpiredBanner(null);
    if (localDevAuthBypass) {
      signInMutation.reset();
      applyLocalDevSuperAdminSession(queryClient);
      router.replace(ROUTES.admin);
      return;
    }
    signInMutation.mutate({
      ...signInValues,
      email: normalizeEmail(signInValues.email),
    });
  };

  const clearSignUpFieldError = (field: keyof SignUpFormValues) => {
    setSignUpFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSignUpSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const data = signUpSchema.validateSync(signUpValues, {
        abortEarly: false,
      });
      setSignUpFieldErrors({});
      signUpMutation.mutate(
        { ...data, email: normalizeEmail(data.email) },
        {
          onSuccess: (res) => {
            if (!res.success) return;
            const email =
              res.data?.email != null && res.data.email !== ''
                ? normalizeEmail(res.data.email)
                : normalizeEmail(data.email);
            setAuthMode('sign-up');
            openVerification(email, RESEND_COOLDOWN_AFTER_SIGNUP_SEC);
            signUpMutation.reset();
          },
        },
      );
    } catch (err) {
      if (err instanceof ValidationError) {
        const next: SignUpFieldErrors = {};
        const issues = err.inner.length > 0 ? err.inner : [err];
        for (const issue of issues) {
          const path = issue.path as keyof SignUpFormValues | undefined;
          if (path && next[path] === undefined) {
            next[path] = issue.message;
          }
        }
        setSignUpFieldErrors(next);
      }
    }
  };

  const handleConfirmSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    resendMutation.reset();
    const email = normalizeEmail(verificationEmail);
    confirmMutation.mutate(
      { email, code: verifyCode.trim() },
      {
        onSuccess: (res) => {
          if (!res.success) return;
          closeVerification();
          setAuthMode('sign-in');
          setVerifiedBanner(
            res.data?.message ??
              'Email verified. You can sign in with your password below.',
          );
        },
      },
    );
  };

  const handleResend = () => {
    const email = normalizeEmail(verificationEmail);
    if (!email || resendCooldownSec > 0) return;
    resendMutation.mutate(
      { email },
      {
        onSuccess: (res) => {
          if (res.success) {
            setResendCooldownSec(RESEND_COOLDOWN_AFTER_RESEND_SEC);
          }
        },
      },
    );
  };

  const signUpHasApiError = Boolean(signUpErrorMessage);
  const signInSuggestsVerify =
    Boolean(signInErrorMessage) &&
    /confirm|verif/i.test(signInErrorMessage ?? '');

  return (
    <AuthSplitLayout
      asideTitle='Easily manage your shelter team'
      asideDescription={
        <>
          Log in to access CRM tools for animals, applicants, and onboarding —
          built for shelters using {themeConfig.brand.shortName}.
        </>
      }
    >
            {verifiedBanner ? (
              <p
                className='mt-8 rounded-lg border border-primary-600/30 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900'
                role='status'
              >
                {verifiedBanner}
              </p>
            ) : null}

            {passwordResetBanner ? (
              <p
                className='mt-8 rounded-lg border border-primary-600/30 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900'
                role='status'
              >
                {passwordResetBanner}
              </p>
            ) : null}

            {emailChangedBanner ? (
              <p
                className='mt-8 rounded-lg border border-primary-600/30 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900'
                role='status'
              >
                {emailChangedBanner}
              </p>
            ) : null}

            {accountDeletedBanner ? (
              <p
                className='mt-8 rounded-lg border border-primary-600/30 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900'
                role='status'
              >
                {accountDeletedBanner}
              </p>
            ) : null}

            {sessionExpiredBanner ? (
              <p
                className='mt-8 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900'
                role='alert'
              >
                {sessionExpiredBanner}
              </p>
            ) : null}

            {verifyOpen ? (
              <>
                <p className='mt-8 text-sm font-medium text-primary-600'>
                  Check your inbox
                </p>
                <h2 className='mt-1 font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'>
                  Verify your email
                </h2>
                <p className='mt-2 text-muted-foreground'>
                  We sent a confirmation code to your email. Enter it below to
                  activate your account, then sign in.
                </p>

                <form className='mt-8 space-y-4' onSubmit={handleConfirmSubmit}>
                  <div className='space-y-1.5'>
                    <label
                      htmlFor='verifyEmail'
                      className='text-sm font-medium text-foreground'
                    >
                      Email
                    </label>
                    <Input
                      id='verifyEmail'
                      type='email'
                      value={verificationEmail}
                      onChange={(e) => setVerificationEmail(e.target.value)}
                      autoComplete='email'
                      required
                      invalid={Boolean(confirmErrorMessage)}
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <label
                      htmlFor='verifyCode'
                      className='text-sm font-medium text-foreground'
                    >
                      Verification code
                    </label>
                    <Input
                      id='verifyCode'
                      type='text'
                      inputMode='numeric'
                      autoComplete='one-time-code'
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      placeholder='Code from email'
                      required
                      invalid={Boolean(confirmErrorMessage)}
                      aria-invalid={Boolean(confirmErrorMessage)}
                    />
                  </div>
                  {confirmErrorMessage ? (
                    <p className='text-sm text-red-600'>
                      {confirmErrorMessage}
                    </p>
                  ) : null}
                  {resendInfoMessage && !resendMutation.isPending ? (
                    <p className='text-sm text-primary-700'>
                      {resendInfoMessage}
                    </p>
                  ) : null}
                  <Button
                    type='submit'
                    variant='secondary'
                    fullWidth
                    isLoading={confirmMutation.isPending}
                  >
                    Verify email
                  </Button>
                  <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between'>
                    <Button
                      type='button'
                      variant='link'
                      className='h-auto min-h-0 p-0 text-sm font-medium text-primary-700 underline decoration-primary-700/30 underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                      disabled={
                        resendCooldownSec > 0 || resendMutation.isPending
                      }
                      onClick={handleResend}
                    >
                      {resendCooldownSec > 0
                        ? `Resend code (${resendCooldownSec}s)`
                        : 'Resend code'}
                    </Button>
                    <Button
                      type='button'
                      variant='link'
                      className='h-auto min-h-0 p-0 text-sm text-muted-foreground hover:text-foreground'
                      onClick={() => {
                        closeVerification();
                        setAuthMode('sign-in');
                      }}
                    >
                      Back to log in
                    </Button>
                  </div>
                </form>
              </>
            ) : authMode === 'sign-up' ? (
              <>
                <p className='mt-8 text-sm font-medium text-primary-600'>
                  New here?
                </p>
                <h2 className='mt-1 font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'>
                  Create an account
                </h2>
                <p className='mt-2 text-muted-foreground'>
                  Add your name and work email to get started with matching
                  adopters and pets.
                </p>

                <form className='mt-8 space-y-4' onSubmit={handleSignUpSubmit}>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-1.5'>
                      <label
                        htmlFor='firstName'
                        className='text-sm font-medium text-foreground'
                      >
                        First name
                      </label>
                      <Input
                        id='firstName'
                        type='text'
                        value={signUpValues.firstName}
                        onChange={(e) => {
                          setSignUpValues((current) => ({
                            ...current,
                            firstName: e.target.value,
                          }));
                          clearSignUpFieldError('firstName');
                        }}
                        placeholder='Jane'
                        autoComplete='given-name'
                        invalid={Boolean(
                          signUpFieldErrors.firstName || signUpHasApiError,
                        )}
                        aria-invalid={Boolean(signUpFieldErrors.firstName)}
                        aria-describedby={
                          signUpFieldErrors.firstName
                            ? 'firstName-error'
                            : undefined
                        }
                      />
                      {signUpFieldErrors.firstName ? (
                        <p
                          id='firstName-error'
                          className='text-sm text-red-600'
                        >
                          {signUpFieldErrors.firstName}
                        </p>
                      ) : null}
                    </div>
                    <div className='space-y-1.5'>
                      <label
                        htmlFor='lastName'
                        className='text-sm font-medium text-foreground'
                      >
                        Last name
                      </label>
                      <Input
                        id='lastName'
                        type='text'
                        value={signUpValues.lastName}
                        onChange={(e) => {
                          setSignUpValues((current) => ({
                            ...current,
                            lastName: e.target.value,
                          }));
                          clearSignUpFieldError('lastName');
                        }}
                        placeholder='Doe'
                        autoComplete='family-name'
                        invalid={Boolean(
                          signUpFieldErrors.lastName || signUpHasApiError,
                        )}
                        aria-invalid={Boolean(signUpFieldErrors.lastName)}
                        aria-describedby={
                          signUpFieldErrors.lastName
                            ? 'lastName-error'
                            : undefined
                        }
                      />
                      {signUpFieldErrors.lastName ? (
                        <p id='lastName-error' className='text-sm text-red-600'>
                          {signUpFieldErrors.lastName}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className='space-y-1.5'>
                    <label
                      htmlFor='signupEmail'
                      className='text-sm font-medium text-foreground'
                    >
                      Work email
                    </label>
                    <Input
                      id='signupEmail'
                      type='email'
                      value={signUpValues.email}
                      onChange={(e) => {
                        setSignUpValues((current) => ({
                          ...current,
                          email: e.target.value,
                        }));
                        clearSignUpFieldError('email');
                      }}
                      placeholder='team@example.org'
                      autoComplete='email'
                      invalid={Boolean(
                        signUpFieldErrors.email || signUpHasApiError,
                      )}
                      aria-invalid={Boolean(signUpFieldErrors.email)}
                      aria-describedby={
                        signUpFieldErrors.email
                          ? 'signupEmail-error'
                          : undefined
                      }
                    />
                    {signUpFieldErrors.email ? (
                      <p
                        id='signupEmail-error'
                        className='text-sm text-red-600'
                      >
                        {signUpFieldErrors.email}
                      </p>
                    ) : null}
                  </div>
                  <div className='space-y-1.5'>
                    <label
                      htmlFor='signupPassword'
                      className='text-sm font-medium text-foreground'
                    >
                      Password
                    </label>
                    <PasswordInput
                      id='signupPassword'
                      value={signUpValues.password}
                      onChange={(e) => {
                        setSignUpValues((current) => ({
                          ...current,
                          password: e.target.value,
                        }));
                        clearSignUpFieldError('password');
                      }}
                      placeholder='Create a password'
                      autoComplete='new-password'
                      invalid={Boolean(
                        signUpFieldErrors.password || signUpHasApiError,
                      )}
                      aria-invalid={Boolean(signUpFieldErrors.password)}
                      aria-describedby={
                        signUpFieldErrors.password
                          ? 'signupPassword-error'
                          : undefined
                      }
                    />
                    {signUpFieldErrors.password ? (
                      <p
                        id='signupPassword-error'
                        className='text-sm text-red-600'
                      >
                        {signUpFieldErrors.password}
                      </p>
                    ) : null}
                    <p className='text-xs text-muted-foreground'>
                      At least 6 characters, one uppercase letter, and one
                      number.
                    </p>
                  </div>
                  {signUpErrorMessage ? (
                    <p className='text-sm text-red-600'>{signUpErrorMessage}</p>
                  ) : null}
                  <Button
                    type='submit'
                    colorTone='primary'
                    appearance='filled'
                    fullWidth
                    isLoading={signUpMutation.isPending}
                  >
                    Sign up
                  </Button>
                </form>

                <p className='mt-6 text-center text-sm text-muted-foreground'>
                  Already have an account?{' '}
                  <button
                    type='button'
                    className='font-semibold text-primary-700 underline decoration-primary-700/30 underline-offset-2 hover:decoration-primary-700'
                    onClick={() => setAuthMode('sign-in')}
                  >
                    Log in
                  </button>
                </p>

                <p className='mt-4 text-center text-sm text-muted-foreground'>
                  Want to try the questionnaire first?{' '}
                  <Link href={ROUTES.matchingPoc} variant='inline'>
                    Open matching quiz
                  </Link>
                </p>
              </>
            ) : (
              <>
                <h1 className='mt-8 font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'>
                  Log in
                </h1>
                <p className='mt-2 text-muted-foreground'>
                  Welcome! Please enter your information to access your account.
                </p>

                <form
                  className='mt-8 space-y-4'
                  onSubmit={handleSignInSubmit}
                  noValidate={localDevAuthBypass}
                >
                  <div className='space-y-1.5'>
                    <label
                      htmlFor='email'
                      className='text-sm font-medium text-foreground'
                    >
                      Email address
                    </label>
                    <Input
                      id='email'
                      type='email'
                      value={signInValues.email}
                      onChange={(e) =>
                        setSignInValues((current) => ({
                          ...current,
                          email: e.target.value,
                        }))
                      }
                      placeholder='example@email.com'
                      autoComplete='email'
                      required
                      invalid={Boolean(signInErrorMessage)}
                      aria-invalid={Boolean(signInErrorMessage)}
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <label
                      htmlFor='password'
                      className='text-sm font-medium text-foreground'
                    >
                      Password
                    </label>
                    <PasswordInput
                      id='password'
                      value={signInValues.password}
                      onChange={(e) =>
                        setSignInValues((current) => ({
                          ...current,
                          password: e.target.value,
                        }))
                      }
                      placeholder='••••••••'
                      autoComplete='current-password'
                      required
                      invalid={Boolean(signInErrorMessage)}
                      aria-invalid={Boolean(signInErrorMessage)}
                    />
                  </div>
                  {signInErrorMessage ? (
                    <p className='text-sm text-red-600'>{signInErrorMessage}</p>
                  ) : null}
                  {signInSuggestsVerify ? (
                    <p className='text-sm text-muted-foreground'>
                      <Button
                        type='button'
                        variant='link'
                        className='h-auto min-h-0 p-0 font-medium text-primary-700 underline decoration-primary-700/30 underline-offset-2 hover:decoration-primary-700'
                        onClick={() => openVerification(signInValues.email, 0)}
                      >
                        Enter email verification code
                      </Button>{' '}
                      for this address.
                    </p>
                  ) : null}
                  <Button
                    type='submit'
                    colorTone='primary'
                    appearance='filled'
                    fullWidth
                    isLoading={signInMutation.isPending}
                  >
                    Log in
                  </Button>
                  <div className='flex justify-center'>
                    <Button
                      type='button'
                      variant='link'
                      className={AUTH_LOGIN_LINK_CLASS}
                      onClick={() => setForgotPasswordOpen(true)}
                    >
                      Forgot password?
                    </Button>
                  </div>
                  {localDevAuthBypass ? (
                    <p
                      className='text-center text-xs text-muted-foreground'
                      role='note'
                    >
                      Local mode: Sign in uses a mock super admin session (no
                      API).
                    </p>
                  ) : null}
                </form>

                {/* <div className='mt-10 space-y-4'>
                  <p className='text-center text-sm text-muted-foreground'>
                    Don&apos;t have an account?
                  </p>
                  <Button
                    type='button'
                    colorTone='orange'
                    appearance='outline'
                    fullWidth
                    onClick={() => setAuthMode('sign-up')}
                  >
                    Sign up
                  </Button>
                </div> */}
              </>
            )}

            <ForgotPasswordDialog
              open={forgotPasswordOpen}
              onOpenChange={setForgotPasswordOpen}
            />
    </AuthSplitLayout>
  );
}
